// Must be the first import
if (process.env.NODE_ENV === 'development') {
  // Must use require here as import statements are only allowed
  // to exist at top-level.
  require('preact/debug');
}

import { render } from 'preact';
import { Suspense, lazy } from 'preact/compat';

import {
  addInitializerClasses,
  attachAttributesToDOM,
  checkBlacklisted,
  fetchWidgetStoreAndAttributes,
  initAnalytics,
  listenDOM,
  parseAttributes,
  reloadWidgetApp,
  saveAdsParamFromUrl,
} from '@app/utils';
import { DivID, QuickQuoteDivID } from '@app/data';
import { locale, loggerCl, errors } from '@app/lib';

const App = lazy(() => import('./App'));
const AppQuickQuote = lazy(() => import('./AppQuickQuote'));

const renderWidget = async (root: HTMLElement) => {
  // Cleanup duplicate declarations
  const shouldReloadWidget = reloadWidgetApp();
  if (shouldReloadWidget) {
    return loggerCl.error(errors.duplicateScript, { forceConsole: true });
  }

  const { storeId } = root.dataset ?? {};

  // Do not load anything if domain/store is blacklisted
  const isBlacklisted = checkBlacklisted(storeId);
  if (isBlacklisted) {
    return console.error(locale.appErrorStoreBlacklisted);
  }

  // Fetch attributes for a store from service
  const data = await fetchWidgetStoreAndAttributes({ storeId });
  if (!data?.store) {
    loggerCl.error(errors.missingStore, {
      data: { storeId },
      forceConsole: true,
    });
  }

  // Set attributes to DOM
  attachAttributesToDOM(root, data?.attributes);

  // Initialize and validate attributes
  const widgetParams = parseAttributes(root.dataset);

  // Setup dependencies
  addInitializerClasses(root, widgetParams);
  initAnalytics();
  saveAdsParamFromUrl();

  render(
    <Suspense fallback={<div />}>
      <App domAttributes={widgetParams} store={data?.store} />
    </Suspense>,
    root
  );
};

const renderQuickQuote = async (root: HTMLElement) => {
  const widgetParams = parseAttributes(root?.dataset || {});
  addInitializerClasses(root, widgetParams);

  render(
    <Suspense fallback={<div />}>
      <AppQuickQuote domAttributes={widgetParams} />
    </Suspense>,
    root
  );
};

listenDOM({
  elementId: DivID,
  onAdd: renderWidget,
  onRemove: (node) => render(null, node),
  onError: () =>
    loggerCl.error(errors.containerNodeMissing, { forceConsole: true }),
});

listenDOM({
  elementId: QuickQuoteDivID,
  onAdd: renderQuickQuote,
  onRemove: (node) => render(null, node),
  onError: () =>
    loggerCl.error(errors.containerNodeMissing, { forceConsole: true }),
});
